import React, { createContext, useContext, useState } from 'react';

import { Text } from '@betterment/samba-react';
import { Flash as InternalFlash, FlashProps as InternalFlashProps } from '@betterment/style-closet-react';

interface FlashValues {
  variant?: InternalFlashProps['variant'];
  message: string;
}

interface FlashContextType {
  flashValues: FlashValues | null;
  setFlashValues: React.Dispatch<React.SetStateAction<FlashValues | null>>;
  message: string | null;
  setMessage: React.Dispatch<React.SetStateAction<string | null>>;
}

const FlashContext = createContext<FlashContextType | null>(null);

export const FlashProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [flashValues, setFlashValues] = useState<FlashValues | null>(null);

  const message = flashValues?.message ?? null;
  const setMessage: FlashContextType['setMessage'] = React.useCallback(
    messageOrMessageResolver => {
      const flashValuesAction: React.SetStateAction<FlashValues | null> = currentFlashValues => {
        const newMessage =
          typeof messageOrMessageResolver === 'function'
            ? messageOrMessageResolver(currentFlashValues?.message ?? null)
            : messageOrMessageResolver;

        if (newMessage) {
          return { variant: undefined, message: newMessage };
        }

        return null;
      };

      setFlashValues(flashValuesAction);
    },
    [setFlashValues]
  );

  const values = React.useMemo(
    () => ({ message, setMessage, flashValues, setFlashValues }),
    [message, flashValues, setMessage, setFlashValues]
  );

  return <FlashContext.Provider value={values}>{children}</FlashContext.Provider>;
};

export const useFlash = (): FlashContextType => {
  const context = useContext<FlashContextType | null>(FlashContext);
  if (!context) {
    throw new Error('useFlash must be used within the FlashProvider');
  }

  return context;
};

interface FlashProps extends Omit<InternalFlashProps, 'variant'> {
  variant?: InternalFlashProps['variant'];
}

export const Flash: React.FC<React.PropsWithChildren<FlashProps>> = ({ onDismiss: _onDismiss, variant, ...rest }) => {
  const { flashValues, setFlashValues } = useFlash();
  const onDismiss = () => {
    if (_onDismiss) {
      _onDismiss();
    }
    setFlashValues(null);
  };

  if (flashValues) {
    return (
      <InternalFlash variant={variant ?? flashValues.variant ?? 'info'} onDismiss={onDismiss} {...rest}>
        <Text>{flashValues.message}</Text>
      </InternalFlash>
    );
  }
  return null;
};
